import { useState } from "react";
import { useFrontofficeContext } from "../../../../context/FrontofficeContext";
import useEvaApi, { EvaApiCall } from "../../../../hooks/useEvaApi";
import EVAApiHelper from "../../../../helpers/EVAApiHelper";
import { useLanguageContext } from "../../../../context/LanguageContext";
import RegistrationCodePageView from "./RegistrationCodePageView";
import useCustomEffect from "../../../../hooks/useCustomEffect";
import useScannerListener from "../../../../hooks/useScannerListener";
import { useTheme } from "styled-components";

function RegistrationCodePage() {
  useScannerListener(onQRScan);
  const callEvaApi = useEvaApi();
  const theme = useTheme();
  const { translate } = useLanguageContext();
  const { pageParams, updatePage, terminal } = useFrontofficeContext();

  const [registrationCode, setRegistrationCode] = useState(
    pageParams.registrationCode ?? "",
  );
  const [error, setError] = useState(null);

  /**
   * UseEffect for checking the registration code
   */
  useCustomEffect(() => {
    if (pageParams.registrationCode) {
      onProceed();
    }
  });

  /**
   * This method will handle the registration code change event
   * @param {event} e
   */
  function onRegistrationCodeChange(e) {
    setRegistrationCode(e.target.value);
  }

  /**
   * This method will handle the QR scan event
   * @param {string} value
   */
  function onQRScan(value) {
    setRegistrationCode(value);
    onProceed(value);
  }

  /**
   * This method will toggle the employee presence based on the current presence status of the employee
   * @param {*} employee
   */
  async function toggleEmployeePresence(employee) {
    const isCurrentlyPresent = employee.present;
    const presenceEndpoint = isCurrentlyPresent ? "sign_out" : "sign_in";

    await callEvaApi(
      new EvaApiCall(`employees/${employee.id}/${presenceEndpoint}`)
        .setMethod("POST")
        .setLoadingGroup("qr_sign_in_fetch")
        .setAlertSuccess(true)
        .setOnSuccess(() => {
          const updatedEmployee = {
            ...employee,
            present: !isCurrentlyPresent,
          };

          updatePage("message", {
            title: translate(
              "eva.frontoffice.pages.employee_registration.label",
            ),
            message: translate(
              "eva.frontoffice.pages.registration_code.employee_presence.label",
              [
                {
                  key: "status",
                  value: (
                    <span
                      style={{
                        color:
                          theme["settings.button.color.primary.background"],
                      }}
                    >
                      {translate(
                        `eva.frontoffice.pages.registration_code.employee_presence.status.${updatedEmployee.present ? "present" : "absent"}`,
                      )}
                    </span>
                  ),
                },
              ],
            ),
          });
        })
        .setOnError((error) => {
          setError(error);
        }),
    );
  }

  /**
   * This method will handle the employee proceed event
   * @param {string} regCode
   */
  async function employeeProceed(regCode = null) {
    regCode = regCode ?? registrationCode;

    await callEvaApi(
      new EvaApiCall("employees")
        .setLoadingGroup("qr_sign_in_fetch")
        .setErrorState(setError)
        .setParams({
          where: [
            {
              column: "registration_code",
              values: [{ value: regCode, operator: "=" }],
            },
          ],
        })
        .setOnSuccess((response) => {
          const employees = response.data.data.records;

          if (employees.length !== 1) {
            setError(
              EVAApiHelper.createResponseError(
                translate(
                  "eva.frontoffice.pages.registration_code.errors.not_found",
                ),
              ),
            );
            return;
          }

          toggleEmployeePresence(employees[0]);
        }),
    );
  }

  /**
   * This method will handle the proceed event
   */
  async function onProceed(regCode = null) {
    regCode = regCode ?? registrationCode;

    //Fetch the visitor by the QR code
    await callEvaApi(
      new EvaApiCall("visitors")
        .setLoadingGroup("qr_sign_in_fetch")
        .setErrorState(setError)
        .setParams({
          with_visitor_visitor_type_data: true,
          with_visitor_fields_data: true,
          with_visitor_question_answers_data: true,
          with_visitor_houserules_data: true,
          with_visitor_external_ids_data: true,
          limit: 1,
          scheduled_only: true,
          where: [
            {
              column: "registration_code",
              values: [{ value: regCode, operator: "=" }],
            },
            {
              column: "visitor_type_id",
              values: [
                {
                  value: terminal?.visitor_types?.map(
                    (tvt) => tvt.visitor_type.id,
                  ),
                  operator: "in",
                },
              ],
            },
          ],
        })
        .setOnSuccess((response) => {
          const visitors = response.data.data.records;

          //If the visitor is not found, try to find an employee with the registration code
          if (visitors.length !== 1) {
            employeeProceed(regCode);
            return;
          }

          //Get the visitor
          const visitor = visitors[0];

          //Go to the visitor type page to handle sign in / sign out
          updatePage("visitor_type", {
            visitor: visitor,
            visitorType: terminal?.visitor_types?.find(
              (tvt) => tvt.visitor_type.id === visitor.visitor_type.id,
            )?.visitor_type,
            signIn: visitor.signed_in_at ? false : true,
            simulated: true,
          });
        }),
    );
  }

  return (
    <RegistrationCodePageView
      registrationCode={registrationCode}
      onRegistrationCodeChange={onRegistrationCodeChange}
      onProceed={onProceed}
      error={error}
    />
  );
}

export default RegistrationCodePage;
